import React, { createContext, useContext, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./OnlineOrderingModal.css";

const ModalContext = createContext({ onHide: () => {} });

function fullscreenClassName(fullscreen) {
	if (!fullscreen) return "";
	if (fullscreen === true) return "modal-fullscreen";
	const map = {
		"sm-down": "modal-fullscreen-sm-down",
		"md-down": "modal-fullscreen-md-down",
		"lg-down": "modal-fullscreen-lg-down",
		"xl-down": "modal-fullscreen-xl-down",
		"xxl-down": "modal-fullscreen-xxl-down",
	};
	if (typeof fullscreen === "string" && map[fullscreen]) {
		return map[fullscreen];
	}
	return "";
}

function sizeClassName(size) {
	if (!size) return "";
	const allowed = { sm: "modal-sm", lg: "modal-lg", xl: "modal-xl" };
	return allowed[size] || "";
}

/**
 * Replaces react-bootstrap Modal; keeps Bootstrap modal CSS classes for theme compatibility.
 */
export function OnlineOrderingModal({
	show,
	onHide,
	centered = false,
	scrollable = false,
	size,
	fullscreen,
	className = "",
	dialogClassName = "",
	contentClassName = "",
	backdropClassName = "",
	backdrop = true,
	children,
}) {
	const handleEscape = useCallback(
		(e) => {
			if (e.key === "Escape" && backdrop !== "static") {
				onHide?.();
			}
		},
		[onHide, backdrop]
	);

	useEffect(() => {
		if (!show) return undefined;
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [show, handleEscape]);

	useEffect(() => {
		if (!show) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [show]);

	if (!show) {
		return null;
	}

	const fs = fullscreenClassName(fullscreen);
	const sz = fs ? "" : sizeClassName(size);
	const dialogClasses = [
		"modal-dialog",
		centered ? "modal-dialog-centered" : "",
		scrollable ? "modal-dialog-scrollable" : "",
		sz,
		fs,
		dialogClassName,
	]
		.filter(Boolean)
		.join(" ");

	const handleBackdropClick = () => {
		if (backdrop !== "static" && backdrop !== false) {
			onHide?.();
		}
	};

	const modalRoot = (
		<>
			{/* Backdrop before dialog so z-index pairs with index.css `.modal-backdrop + .modal` and paints under the shell */}
			{backdrop !== false && (
				<div
					className={`modal-backdrop fade show oo-portal-modal-backdrop ${backdropClassName}`.trim()}
					aria-hidden="true"
					onClick={handleBackdropClick}
				/>
			)}
			<div
				className={`modal fade show d-block oo-portal-modal ${className}`.trim()}
				role="dialog"
				aria-modal="true"
				tabIndex={-1}
				onClick={handleBackdropClick}
			>
				<div className={dialogClasses} onClick={(e) => e.stopPropagation()}>
					<div className={`modal-content ${contentClassName}`.trim()}>{children}</div>
				</div>
			</div>
		</>
	);

	return createPortal(
		<ModalContext.Provider value={{ onHide }}>{modalRoot}</ModalContext.Provider>,
		document.body
	);
}

function ModalHeader({ closeButton, className = "", children }) {
	const { onHide } = useContext(ModalContext);
	return (
		<div className={`modal-header ${className}`.trim()}>
			{children}
			{closeButton ? (
				<button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
			) : null}
		</div>
	);
}

function ModalTitle({ as: Component = "h5", className = "", children }) {
	return <Component className={`modal-title ${className}`.trim()}>{children}</Component>;
}

function ModalBody({ className = "", children }) {
	return <div className={`modal-body ${className}`.trim()}>{children}</div>;
}

function ModalFooter({ className = "", children }) {
	return <div className={`modal-footer ${className}`.trim()}>{children}</div>;
}

OnlineOrderingModal.Header = ModalHeader;
OnlineOrderingModal.Title = ModalTitle;
OnlineOrderingModal.Body = ModalBody;
OnlineOrderingModal.Footer = ModalFooter;

export default OnlineOrderingModal;
